<template>
  <div v-if="game" class="board-screen">
    <!-- Async room: who the game is waiting on (src/store/asyncPlay.js). -->
    <p v-if="waitingBanner" class="waiting-banner" :class="{ 'is-mine': waitingOnMe }">{{ waitingBanner }}</p>
    <!-- score + turn -->
    <div class="board-head">
      <span class="team-count team-count--red" :class="{ 'is-active': game.turn.team === 'red' }">
        {{ wordsLeft.red }}
      </span>
      <div class="board-head__mid">
        <template v-if="finished">
          <span class="turn-line">
            <strong :class="`ink-${game.meta.winner}`">{{ teamName(game.meta.winner) }}</strong>
            wins{{ game.meta.endedBy === 'assassin' ? ' — the assassin!' : '' }}
          </span>
        </template>
        <template v-else-if="game.turn.phase === 'clue'">
          <span class="turn-line">
            <strong :class="`ink-${game.turn.team}`">{{ teamName(game.turn.team) }}</strong>
            {{ iGiveClue ? '— give your clue' : 'spymaster is thinking…' }}
          </span>
        </template>
        <template v-else>
          <span class="turn-line">
            <strong :class="`ink-${game.turn.team}`">{{ game.turn.clue.word }}</strong>
            · {{ clueCountLabel }}
            <span class="muted"> — {{ guessesLeftLabel }}</span>
          </span>
        </template>
      </div>
      <span class="team-count team-count--blue" :class="{ 'is-active': game.turn.team === 'blue' }">
        {{ wordsLeft.blue }}
      </span>
    </div>

    <!-- the grid -->
    <div class="board" :class="{ 'board--keyed': showKey }">
      <button
        v-for="(word, i) in game.board"
        :key="word"
        class="codename"
        :class="cardClass(i)"
        :disabled="!canTouch(i)"
        @click="touch(i)"
      >
        <span class="codename__word">{{ word }}</span>
        <span v-if="pending === i" class="codename__confirm">Contact?</span>
      </button>
    </div>

    <!-- my controls -->
    <div class="board-controls">
      <form v-if="iGiveClue" class="clue-form" @submit.prevent="submitClue">
        <input
          v-model="clueWord"
          class="clue-form__word"
          placeholder="One-word clue"
          autocomplete="off"
          spellcheck="false"
          maxlength="30"
        />
        <div class="clue-form__numbers">
          <button
            v-for="n in numberChoices"
            :key="n"
            type="button"
            class="clue-form__number"
            :class="{ 'is-selected': n === clueCount }"
            @click="clueCount = n"
          >{{ n === 'unlimited' ? '∞' : n }}</button>
        </div>
        <button class="primary" type="submit" :disabled="!clueWord.trim()">Give clue</button>
      </form>

      <div v-else-if="iGuess" class="guess-controls">
        <button
          v-if="pending !== null"
          class="primary guess-controls__contact"
          @click="confirmGuess"
        >Touch {{ game.board[pending] }}</button>
        <p v-else class="muted">
          Tap a card to make contact.
        </p>
        <button
          class="secondary"
          :disabled="game.turn.guessedThisTurn < 1"
          @click="stop"
        >Stop guessing</button>
      </div>

      <p v-else-if="!finished" class="muted board-controls__waiting">
        <template v-if="game.turn.phase === 'guess' && game.turn.team === myTeam && isSpymaster">
          Keep a straight face. Your operatives are choosing.
        </template>
        <template v-else>
          {{ teamName(game.turn.team) }} is playing.
        </template>
      </p>

      <div v-if="finished" class="board-controls__over">
        <button v-if="isHost" class="primary" @click="$store.dispatch('deal')">Play again</button>
        <button v-if="isHost" class="secondary" @click="$store.dispatch('returnToLobby')">Change teams</button>
        <p v-else class="muted">Waiting for the host to deal again…</p>
      </div>
    </div>

    <!-- the table's memory -->
    <div class="board-log">
      <p v-for="(line, i) in recentLog" :key="i" class="board-log__line">
        <template v-if="line.kind === 'clue'">
          <strong :class="`ink-${line.team}`">{{ line.word }}</strong> · {{ line.count === 'unlimited' ? '∞' : line.count }}
        </template>
        <template v-else-if="line.kind === 'guess'">
          {{ line.word }} — <span :class="identityInk(line.identity)">{{ identityLabel(line.identity) }}</span>
        </template>
        <template v-else-if="line.kind === 'pass'">
          {{ teamName(line.team) }} stopped.
        </template>
        <template v-else-if="line.kind === 'end'">
          <strong :class="`ink-${line.winner}`">{{ teamName(line.winner) }}</strong>
          wins{{ line.by === 'assassin' ? ' by assassin' : '' }}.
        </template>
      </p>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import { waitingLine, waitingOnMe as isWaitingOnMe } from '@/store/asyncPlay.js';
import { UNLIMITED, MAX_CLUE_NUMBER } from '@/engine/constants';

export default {
  name: 'GameBoard',
  data() {
    return {
      clueWord: '',
      clueCount: 2,
      pending: null, // tapped-but-unconfirmed card index
    };
  },
  computed: {
    waitingOnMe() { return isWaitingOnMe(this.$store.state.roomMeta, this.$store.state.user?.uid); },
    waitingBanner() {
      const meta = this.$store.state.roomMeta;
      const game = this.game;
      if (!meta?.async || !game) return '';
      const nameOf = (id) => (game.players || []).find((p) => p.id === id)?.name || 'someone';
      return waitingLine(meta, this.$store.state.user?.uid, nameOf);
    },
    ...mapState(['game']),
    ...mapGetters(['iGiveClue', 'iGuess', 'isSpymaster', 'myTeam', 'wordsLeft', 'finished', 'isHost']),
    // Spymasters always see the key; when the game ends, everyone does
    // ("just flip over the 25 codenames", p5 - the table always looks).
    showKey() { return this.isSpymaster || this.finished; },
    numberChoices() {
      return [...Array.from({ length: MAX_CLUE_NUMBER }, (_, i) => i + 1), 0, UNLIMITED];
    },
    clueCountLabel() {
      const c = this.game.turn.clue.count;
      return c === UNLIMITED ? '∞' : c;
    },
    guessesLeftLabel() {
      const left = this.game.turn.guessesLeft;
      return left === null ? 'guess freely' : `${left} guess${left === 1 ? '' : 'es'} left`;
    },
    recentLog() {
      return [...(this.game.log || [])].slice(-6).reverse();
    },
  },
  watch: {
    'game.turn.clue': function onClue() { this.pending = null; },
    'game.turn.team': function onTeam() { this.pending = null; this.clueWord = ''; },
  },
  methods: {
    teamName: (team) => (team === 'red' ? 'Red' : 'Blue'),
    identityLabel(identity) {
      if (identity === 'bystander') return 'an innocent bystander';
      if (identity === 'assassin') return 'the ASSASSIN';
      return `a ${identity} agent`;
    },
    identityInk(identity) {
      if (identity === 'red' || identity === 'blue') return `ink-${identity}`;
      return identity === 'assassin' ? 'ink-assassin' : 'ink-bystander';
    },
    cardClass(i) {
      const classes = [];
      if (this.game.revealed[i]) {
        classes.push('codename--revealed', `codename--${this.game.key[i]}`);
      } else if (this.showKey) {
        classes.push(`codename--key-${this.game.key[i]}`);
      }
      if (this.pending === i) classes.push('codename--pending');
      return classes;
    },
    canTouch(i) {
      return this.iGuess && !this.game.revealed[i] && !this.finished;
    },
    touch(i) {
      this.pending = this.pending === i ? null : i;
    },
    confirmGuess() {
      const index = this.pending;
      this.pending = null;
      this.$store.dispatch('guess', index);
    },
    stop() {
      this.pending = null;
      this.$store.dispatch('endTurn');
    },
    submitClue() {
      this.$store.dispatch('giveClue', { word: this.clueWord, count: this.clueCount });
      this.clueWord = '';
    },
  },
};
</script>

<style scoped>
.waiting-banner { margin: 0; padding: 0.5rem 0.9rem; border-radius: 0.6rem; background: rgba(0,0,0,0.08); font-size: 0.95rem; }
.waiting-banner.is-mine { background: rgba(0,0,0,0.16); font-weight: 700; }
</style>
